import { getPresignedImageUrl } from "@app/utils";
import {
  cropImageBlob,
  OBJECT_FIT,
  OBJECT_POSITION,
} from "@app/utils/imageBlob";
import { uploadFileByBlob } from "./s3";

const getSlideVisual = (slide) => {
  let visual = { ...slide.visuals?.[slide.visual] };

  if (!visual) {
    return null;
  }

  if (visual.type === "firefly" && visual.libraryId) {
    // FIXME: fix duplicated visuals from CC library
    visual = { ...slide.visuals?.[slide.visual - 1] };
  }

  return visual;
};

const getMediaImage = async (
  visual,
  objectFit = OBJECT_FIT.COVER,
  objectPosition = OBJECT_POSITION.CENTER,
  containerSize = {},
) => {
  if (!visual) {
    return {};
  }

  if (visual.type === "cc-library") {
    visual.url = await getPresignedImageUrl(visual.libraryId, visual.id);
  }

  const imageId =
    Math.floor(Math.random() * (9999999999 - 1000000000 + 1)) + 1000000000;
  /*
   source: "firefly",
        id: `${imageId}`,
        url: slide.visuals?.[0]?.url,
        width: 1920,
        height: 1080,
        prompt: slide.visuals?.[0]?.prompt,
        seed: slide.visuals?.[0]?.seed,
        style: "",*/

  // Validate URL before including in manifest
  if (!visual.url) {
    console.warn("Visual has no URL, skipping media inclusion");
    return {};
  }

  // Log the URL for debugging
  console.log("Including visual with URL:", visual.url);

  const ret = {
    media: {
      type: "image",
      data: {
        source: "plain",
        id: `${imageId}`,
        url: visual.url,
        width: 2688,
        height: 1536,
      },
    },
  };

  if (objectFit === OBJECT_FIT.CONTAIN) {
    const blob = await fetch(visual.url).then((res) => res.blob());
    const croppedBlob = await cropImageBlob(
      blob,
      containerSize,
      OBJECT_FIT.CONTAIN,
      objectPosition,
    );
    const croppedUrl = await uploadFileByBlob();
    // ret.media.data.saliency = fourCornerSaliency(48, 48, {
    //   // sigma: 0.0001,
    //   // normalize: "minmax",
    // });
    // TODO: crop image to fit within container
  }

  return ret;
};

const getSlides = async (story) => {
  const slides = story.script.slice(2, -1).map((slide) => ({
    ...slide,
    personas: slide?.personas?.map((idx) => story.personas[idx]) ?? [],
  }));

  const presentation = {
    version: "1.0.0",
    slides: [],
  };

  const [coverVisual, introVisual] = await Promise.all([
    getMediaImage(getSlideVisual(story.script[0])),
    getMediaImage(getSlideVisual(story.script[1])),
  ]);

  const coverSlide = {
    archetype: "cover",
    index: 0,
    title: story.script[0].title,
    subtitle: "Customer Journey",
    ...coverVisual,
  };

  const introSlide = {
    archetype: "small_title",
    index: 1,
    title: story.script[1].title,
    chapter: story.script[0].title,
    ...introVisual,
  };

  const agendaSlide = {
    archetype: "agenda",
    index: 2,
    chapter: story.script[0].title,
    agenda: Object.entries(
      slides.reduce((acc, slide) => {
        if (slide.section !== slide.title) {
          acc[slide.section] = acc[slide.section] || [];
          if (slide.title) {
            acc[slide.section].push(slide.title);
          }
        } else {
          acc[slide.title] = [];
        }
        return acc;
      }, {}),
    ).map(([chapter, topics]) => ({
      chapter,
      topics,
    })),
  };

  // Only add customContent if introVisual has media
  if (introVisual?.media) {
    agendaSlide.customContent = [
      {
        role: "custom:visual",
        type: "media",
        data: introVisual.media,
      },
    ];
  }

  presentation.slides.push(coverSlide, introSlide, agendaSlide);

  // TODO: check selected brand and fetch logo
  // const logo = getMediaImage(logoAsset, OBJECT_FIT.CONTAIN, OBJECT_POSITION.LEFT, { width: 421, height: 112 });
  const logo = {};

  let section_id;

  for (let idx = 0; idx < slides.length; idx++) {
    const { section, title, text, personas = [], visuals } = slides[idx];
    let archetype;

    const visualUrl = visuals?.[0]?.url;
    const visualSeed = visuals?.[0]?.seed;
    const visualPrompt = visuals?.[0]?.prompt;

    const index = idx + 3;

    if (section !== slides?.[idx - 1]?.section) {
      archetype = "chapter";
      section_id = crypto.randomUUID();
    } else {
      archetype = "content";
    }

    if (personas.some((persona) => title.includes(persona.name))) {
      archetype = "custom:personaIntroduction";
    }

    const slide = {
      archetype,
      index,
      chapter: `${story.script[0].title}   |   ${section}`,
      section_id,
      title,
    };

    // TODO: add support for icons
    const [slideVisual, slideIcon] = await Promise.all([
      getMediaImage(getSlideVisual(slides[idx])),
      Promise.resolve({}),
    ]);

    switch (archetype) {
      case "chapter":
        slide.subtitle = text;

        if (slideVisual.media) {
          slide.media = slideVisual.media;
        }

        break;

      case "custom:personaIntroduction":
        slide.customContent = [
          {
            role: "custom:chapter",
            type: "text",
            data: {
              // heading: slide.chapter,
              // points: [{ point: "" }],
              points: [{ point: slide.chapter }],
              // points: [slide.chapter],
            },
          },
          {
            role: "custom:title",
            type: "text",
            data: {
              // heading: title,
              // points: [{ point: "" }],
              points: [{ point: title }],
              // points: [title],
            },
          },
          {
            role: "custom:subtitle",
            type: "text",
            data: {
              // heading: personas[0]?.role || "",
              points: [{ point: personas[0]?.role || "" }],
              // points: [personas[0]?.role || ""],
            },
          },
          {
            role: "custom:content",
            type: "text",
            data: {
              // heading: personas[0]?.role || "",
              points: !!text
                ? text?.split("\n").map((point) => ({ point }))
                : [],
              // points: !!text ? text?.split("\n").map((point) => point) : [],
            },
          },
          // {
          //   role: "custom:logo",
          //   type: "media",
          //   data: logo?.media,
          // },
          // {
          //   role: "custom:icon",
          //   type: "media",
          //   data: slideIcon?.media,
          // },
        ];

        [
          [slideVisual, "visual"],
          [slideIcon, "icon"],
          [logo, "logo"],
        ].forEach(([visualItem, role]) => {
          if (visualItem.media) {
            slide.customContent.push({
              role: `custom:${role}`,
              type: "media",
              data: visualItem.media,
            });
          }
        });

        // delete slide.title;
        // delete slide.chapter;

        break;
      case "content":
      default:
        slide.content = [
          {
            //   text: { heading: text },
            text: {
              // heading: text,
              // points: !!text
              //   ? text?.split("\n").map((point) => ({ point }))
              //   : [],
              // points: !!text
              //   ? `${text}\n${text}`?.split("\n").map((point) => ({ point }))
              //   : [],
              points: !!text ? [{ point: text }] : [],
            },
            ...slideVisual,
          },
        ];

        slide.layout_preference = "column";

        if (personas.length > 0) {
          slide.customContent = [
            {
              role: "custom:personaName",
              type: "text",
              data: {
                points: [{ point: personas[0]?.name || "" }],
              },
            },
            {
              role: "custom:personaRole",
              type: "text",
              data: {
                points: [{ point: personas[0]?.role || "" }],
              },
            },
          ];

          const personaAvatar = await getMediaImage(
            getSlideVisual(slides[idx]),
          );

          if (personaAvatar.media) {
            slide.customContent.push({
              role: "custom:personaAvatar",
              type: "media",
              data: personaAvatar.media,
            });
          }
        }

        break;
    }

    if (section.toLowerCase().includes("introduction")) {
      personas.forEach((personaIdx) => {
        const persona = story.personas[personaIdx];
        if (slide.title.toLowerCase().includes(persona?.name?.toLowerCase())) {
          slide.subtitle = persona?.role;
        }
      });
    }

    presentation.slides.push(slide);
  }

  const outroSlide = {
    archetype: "small_title",
    index: presentation.slides.length,
    title: "",
  };

  // const outroMedia = getMediaImage(
  //   ({ visualUrl, visualSeed, visualPrompt } =
  //     story.script[story.script.length - 1]),
  // );

  // if (outroMedia.media) {
  //   outroSlide.media = outroMedia.media;
  // }

  presentation.slides.push(outroSlide);

  presentation.state = "DONE";

  // Validate all image URLs in the presentation
  const allUrls = [];
  presentation.slides.forEach((slide, idx) => {
    // Check direct media
    if (slide.media?.data?.url) {
      allUrls.push({ slide: idx, type: "media", url: slide.media.data.url });
    }
    // Check customContent
    if (slide.customContent) {
      slide.customContent.forEach((content, contentIdx) => {
        if (content.type === "media" && content.data?.data?.url) {
          allUrls.push({
            slide: idx,
            type: `customContent[${contentIdx}]`,
            url: content.data.data.url,
          });
        }
      });
    }
    // Check content
    if (slide.content) {
      slide.content.forEach((item, itemIdx) => {
        if (item.media?.data?.url) {
          allUrls.push({
            slide: idx,
            type: `content[${itemIdx}]`,
            url: item.media.data.url,
          });
        }
      });
    }
  });

  console.log("=== Presentation Validation ===");
  console.log(`Total slides: ${presentation.slides.length}`);
  console.log(`Total image URLs: ${allUrls.length}`);
  allUrls.forEach(({ slide, type, url }) => {
    console.log(`  Slide ${slide} (${type}): ${url.substring(0, 100)}...`);
  });
  console.log("=== End Validation ===");

  return presentation;
};

export const getStoryPresentation = async (story) => await getSlides(story);
